import { useState, useEffect } from 'react';
import { administratorsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { UserCog, Plus, Mail, Trash2, ShieldCheck, Loader, X } from 'lucide-react';

export default function Administrators() {
  const { user } = useAuth();
  const currentUser = user;
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ email: '', full_name: '' });
  const [saving, setSaving]     = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await administratorsApi.list();
      setAdmins(data);
    } catch { toast.error('Failed to load administrators'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSaving(true);
    try {
      await administratorsApi.create(form);
      toast.success(`Administrator account created for ${form.email}`);
      setForm({ email: '', full_name: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create administrator');
    } finally { setSaving(false); }
  };

  const handleSendInvite = async (admin) => {
    try {
      await administratorsApi.sendInvite(admin.id);
      toast.success(`Invite sent to ${admin.email}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleRemove = async (admin) => {
    if (!window.confirm(`Remove administrator ${admin.email}?\n\nThis cannot be undone.`)) return;
    try {
      await administratorsApi.remove(admin.id);
      toast.success('Administrator removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove administrator');
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Administrators</h1>
          <p className="page-subtitle">
            Global Admin accounts have full platform access and are not associated with any tenant.
          </p>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="btn btn-primary">
          {showForm ? <><X size={15} /> Cancel</> : <><Plus size={15} /> Add Administrator</>}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">New Administrator</div>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label className="input-label">Email address</label>
                <input
                  type="email"
                  className="input"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="admin@company.com"
                  required
                  autoFocus
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="input-label">Full name <span style={{ color: 'var(--text-dim)', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <input
                  type="text"
                  className="input"
                  value={form.full_name}
                  onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                  placeholder="Jane Smith"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '10px 14px', background: 'rgba(37,99,235,0.08)', borderRadius: 8, border: '1px solid rgba(37,99,235,0.2)', fontSize: 13, color: 'var(--text-mid)' }}>
              <ShieldCheck size={15} style={{ color: 'var(--blue)', flexShrink: 0, marginTop: 1 }} />
              <span>The account will be created with a random password. Use <strong>Send Invite</strong> after creation to email them a set-password link.</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={saving || !form.email.trim()} className="btn btn-primary">
                {saving ? <><Loader size={14} className="spin" /> Creating…</> : 'Create Administrator'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Admins list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {admins.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <UserCog size={36} style={{ color: 'var(--text-dim)', margin: '0 auto 12px' }} />
            <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No administrators found.</p>
          </div>
        ) : (
          admins.map((admin, idx) => (
            <div
              key={admin.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: idx < admins.length - 1 ? '1px solid var(--navy-border)' : 'none',
                background: admin.id === currentUser?.id ? 'rgba(37,99,235,0.04)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--navy)', border: '1px solid var(--navy-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: 'var(--blue)', flexShrink: 0,
                }}>
                  {(admin.full_name || admin.email)[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                      {admin.full_name || admin.email}
                    </span>
                    {admin.id === currentUser?.id && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(37,99,235,0.12)', color: 'var(--blue)', border: '1px solid rgba(37,99,235,0.25)', fontWeight: 600 }}>
                        You
                      </span>
                    )}
                    {admin.activated_at ? (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(34,197,94,0.1)', color: 'var(--green)', border: '1px solid rgba(34,197,94,0.2)', fontWeight: 600 }}>
                        Active
                      </span>
                    ) : (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.1)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,0.2)', fontWeight: 600 }}>
                        Pending
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>
                    {admin.full_name ? admin.email : ''}
                    {admin.activated_at
                      ? ` · Active since ${new Date(admin.activated_at).toLocaleDateString('en-AU')}`
                      : ' · Invite not yet accepted'}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {!admin.activated_at && (
                  <button
                    onClick={() => handleSendInvite(admin)}
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--blue)', borderColor: 'rgba(37,99,235,0.3)' }}
                  >
                    <Mail size={13} />
                    {admin.invite_pending ? 'Resend Invite' : 'Send Invite'}
                  </button>
                )}
                {admin.id !== currentUser?.id && (
                  <button
                    onClick={() => handleRemove(admin)}
                    className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.3)' }}
                    title="Remove administrator"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info */}
      <div style={{ marginTop: 14, padding: '10px 16px', borderRadius: 8, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', fontSize: 12, color: 'var(--text-dim)', display: 'flex', gap: 8 }}>
        <ShieldCheck size={13} style={{ color: 'var(--blue)', marginTop: 1, flexShrink: 0 }} />
        <span>Global Admins have full access to all tenants, can manage connections and file mappings on any tenant, and can create or remove other Global Admins. They are not associated with any tenant.</span>
      </div>
    </div>
  );
}
